/**
 * Hook pour la gestion des préférences utilisateur.
 * Persiste dans localStorage sous la clé 'wt_neo_preferences'.
 */

import { useState, useCallback, useEffect } from 'react';
import type { UserPreferences } from '../types/app';
import { DEFAULT_PREFERENCES } from '../types/app';

const STORAGE_KEY = 'wt_neo_preferences';

const OVERLAY_TO_BASE = new Set([
  'cards-backs', 'cards-all', 'cards-trumps',
  'cards-backs-dark', 'cards-all-dark', 'cards-trumps-dark',
  'kaleidoscope', 'kaleidoscope-v2',
]);

const DARK_TO_NORMAL: Record<string, string> = {
  'cards-backs-dark': 'cards-backs',
  'cards-all-dark': 'cards-all',
  'cards-trumps-dark': 'cards-trumps',
};

function migratePreferences(prefs: Record<string, unknown>): Record<string, unknown> {
  const overlay = prefs.tableOverlay as string | undefined;
  if (overlay && OVERLAY_TO_BASE.has(overlay)) {
    prefs.tableBase = DARK_TO_NORMAL[overlay] ?? overlay;
    prefs.tableOverlay = 'none';
  }
  return prefs;
}

function loadPreferences(): UserPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = migratePreferences(JSON.parse(stored));
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
  return { ...DEFAULT_PREFERENCES };
}

function savePreferences(prefs: UserPreferences): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function useUserPreferences() {
  const [preferences, setPreferences] = useState<UserPreferences>(loadPreferences);

  // Persister quand les préférences changent
  useEffect(() => {
    savePreferences(preferences);
  }, [preferences]);

  const updatePreferences = useCallback((partial: Partial<UserPreferences>) => {
    setPreferences(prev => ({ ...prev, ...partial }));
  }, []);

  return { preferences, updatePreferences };
}
